import { useState } from "react";
import { useGameStore } from "../state/useGameStore";
import { VerbIcon } from "./VerbIcon";
import type { InventoryItem, VerbEntry } from "../engine/SandboxEngine";

function EntityRow({ id, name, verbs, disabled, onVerb }: {
  id: string;
  name: string;
  verbs: VerbEntry[];
  disabled: boolean;
  onVerb: (entityId: string, verb: string) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.3rem", flexWrap: "wrap" }}>
      <span style={{
        background: "var(--theme-lozenge-bg)",
        color: "var(--theme-lozenge-color)",
        padding: "0.15rem 0.4rem",
        borderRadius: "3px",
        fontSize: "0.75rem",
        fontStyle: "italic",
        fontWeight: "600",
        whiteSpace: "nowrap",
        display: "inline-block",
      }}>
        {name}
      </span>
      <div style={{ display: "flex", gap: "0.2rem" }}>
        {verbs.map((v) => (
          <VerbIcon
            key={v.name}
            verb={v.name}
            size={12}
            disabled={disabled}
            locked={!v.available}
            {...(v.lockReason !== undefined && { lockReason: v.lockReason })}
            onClick={() => onVerb(id, v.name)}
          />
        ))}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: "0.8rem",
      fontWeight: "600",
      opacity: 0.85,
      marginTop: "0.5rem",
      marginBottom: "0.25rem",
      color: "var(--theme-text-color)",
    }}>
      {children}
    </div>
  );
}

function InventoryPanel({
  items,
  capacity,
  disabled,
  onVerb,
  assembleMode,
  assembleFirst,
  onAssembleSelect,
  onCancelAssemble,
  getVerbEntries
}: {
  items: InventoryItem[];
  capacity: { used: number; max: number };
  disabled: boolean;
  onVerb: (entityId: string, verb: string) => void;
  assembleMode: boolean;
  assembleFirst: string | null;
  onAssembleSelect: (itemId: string) => void;
  onCancelAssemble: () => void;
  getVerbEntries: (entityId: string) => VerbEntry[];
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
        <span style={{ fontSize: "0.65rem", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Inventory
        </span>
        <span style={{ fontSize: "0.65rem", opacity: 0.6, fontVariantNumeric: "tabular-nums" }}>
          {capacity.used}/{capacity.max}
        </span>
      </div>

      {assembleMode && (
        <div style={{ marginBottom: "0.5rem", padding: "0.4rem", background: "rgba(255, 180, 80, 0.12)", border: "1px solid rgba(255, 180, 80, 0.2)", borderRadius: "4px", fontSize: "0.75rem" }}>
          <span>Combine with...</span>
          <button
            onClick={onCancelAssemble}
            className="theme-button"
            style={{
              display: "block",
              marginTop: "0.3rem",
              fontSize: "0.65rem",
              padding: "0.1rem 0.3rem",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", paddingRight: "0.2rem" }}>
        {items.length === 0 ? (
          <div style={{ fontSize: "0.75rem", opacity: 0.35, fontStyle: "italic", padding: "0.5rem 0" }}>Empty</div>
        ) : (
          items.map((item) => {
            const isFirst = assembleMode && item.id === assembleFirst;
            const isSelectable = assembleMode && !isFirst;
            const verbEntries = getVerbEntries(item.id);

            return (
              <div
                key={item.id}
                onClick={isSelectable ? () => onAssembleSelect(item.id) : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  marginBottom: "0.3rem",
                  flexWrap: "wrap",
                  cursor: isSelectable ? "pointer" : undefined,
                  padding: isSelectable ? "0.2rem" : undefined,
                  background: isFirst
                    ? "rgba(255, 180, 80, 0.15)"
                    : isSelectable
                      ? "rgba(255, 255, 255, 0.04)"
                      : undefined,
                  borderRadius: "4px",
                  border: isFirst ? "1px solid rgba(255, 180, 80, 0.3)" : undefined,
                  transition: "background 0.15s ease",
                }}
              >
                <span style={{
                  background: "var(--theme-lozenge-bg)",
                  color: "var(--theme-lozenge-color)",
                  padding: "0.15rem 0.4rem",
                  borderRadius: "3px",
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                  fontWeight: "600",
                  display: "inline-flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                }}>
                  {item.icon && (
                    <img
                      src={item.icon}
                      alt=""
                      style={{ width: 12, height: 12, marginRight: "0.25rem", verticalAlign: "middle" }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  {item.name}
                </span>
                {!assembleMode && (
                  <div style={{ display: "flex", gap: "0.2rem" }}>
                    {verbEntries.map((v) => (
                      <VerbIcon
                        key={v.name}
                        verb={v.name}
                        size={12}
                        disabled={disabled}
                        locked={!v.available}
                        onClick={() => onVerb(item.id, v.name)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export function SandboxPanel() {
  const room = useGameStore((s) => s.sandboxRoom);
  const narrationQueue = useGameStore((s) => s.sandboxNarrationQueue);
  const sandboxDispatch = useGameStore((s) => s.sandboxDispatch);
  const dismissNarration = useGameStore((s) => s.sandboxDismissNarration);
  const sandboxEngine = useGameStore((s) => s.sandboxEngine);
  const inventory = useGameStore((s) => s.sandboxInventory);
  const capacity = useGameStore((s) => s.sandboxCapacity);
  const assembleMode = useGameStore((s) => s.sandboxAssembleMode);
  const assembleFirst = useGameStore((s) => s.sandboxAssembleFirst);
  const completeAssemble = useGameStore((s) => s.sandboxCompleteAssemble);
  const cancelAssemble = useGameStore((s) => s.sandboxCancelAssemble);
  const giveMode = useGameStore((s) => s.sandboxGiveMode);
  const completeGive = useGameStore((s) => s.sandboxCompleteGive);
  const cancelGive = useGameStore((s) => s.sandboxCancelGive);
  const sandboxTime = useGameStore((s) => s.sandboxTime);
  const sandboxWait = useGameStore((s) => s.sandboxWait);

  const [isInventoryHovered, setIsInventoryHovered] = useState(false);
  const [inventoryPinned, setInventoryPinned] = useState(false);

  if (!room) return null;

  const currentNarration = narrationQueue.length > 0 ? narrationQueue[0] : null;
  const hasNarration = !!currentNarration;

  // Inventory is pinned open if combining, giving items, or user pinned it
  const showInventoryPanel = isInventoryHovered || assembleMode || giveMode || inventoryPinned;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 40,
      }}
    >
      {/* Clock readout (top-center) with quick "wait" presets. Only when the
          game uses time (sandboxTime is null for calendar-less games). */}
      {sandboxTime && (
        <div
          style={{
            position: "absolute",
            top: "1.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            pointerEvents: "auto",
            zIndex: 60,
          }}
        >
          <span
            className="theme-panel"
            style={{
              padding: "0.3rem 0.7rem",
              fontSize: "0.85rem",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {sandboxTime}
          </span>
          {!hasNarration && (
            <>
              <button
                className="theme-button"
                title="Wait 15 minutes"
                style={{ fontSize: "0.65rem", padding: "0.2rem 0.4rem" }}
                onClick={() => sandboxWait(15)}
              >
                +15m
              </button>
              <button
                className="theme-button"
                title="Wait 1 hour"
                style={{ fontSize: "0.65rem", padding: "0.2rem 0.4rem" }}
                onClick={() => sandboxWait(60)}
              >
                +1h
              </button>
            </>
          )}
        </div>
      )}

      {/* Narration area — dialogue box style */}
      {currentNarration && (
        <div
          onClick={dismissNarration}
          className="theme-panel"
          style={{
            position: "absolute",
            bottom: "2rem",
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(80%, 720px)",
            padding: "1.25rem 1.75rem",
            cursor: "pointer",
            userSelect: "none",
            pointerEvents: "auto",
            zIndex: 100,
          }}
        >
          <p style={{ margin: 0, fontSize: "1rem", lineHeight: 1.6 }}>{currentNarration}</p>
          <span
            style={{
              display: "block",
              textAlign: "right",
              opacity: 0.35,
              fontSize: "0.75rem",
              marginTop: "0.4rem",
            }}
          >
            {narrationQueue.length > 1
              ? `Click to continue (${narrationQueue.length - 1} more)`
              : "Click to dismiss"}
          </span>
        </div>
      )}

      {/* Floating Room Info Panel (Left) */}
      <div
        className="theme-panel"
        style={{
          position: "absolute",
          left: "1.5rem",
          top: "1.5rem",
          width: "280px",
          padding: "0.75rem 1rem",
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          maxHeight: "calc(100% - 3rem)",
          pointerEvents: "auto",
        }}
      >
        <div style={{ flex: 1, overflowY: "auto", paddingRight: "0.2rem" }}>
          {/* Location category */}
          <div>
            <SectionLabel>Location:</SectionLabel>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.3rem", flexWrap: "wrap" }}>
              <span style={{
                background: "var(--theme-lozenge-bg)",
                color: "var(--theme-lozenge-color)",
                padding: "0.15rem 0.4rem",
                borderRadius: "3px",
                fontSize: "0.75rem",
                fontStyle: "italic",
                fontWeight: "600",
                whiteSpace: "nowrap",
                display: "inline-block",
              }}>
                {room.name}
              </span>
              <div style={{ display: "flex", gap: "0.2rem" }}>
                {room.roomVerbs.map((v) => (
                  <VerbIcon
                    key={v.name}
                    verb={v.name}
                    size={12}
                    disabled={hasNarration}
                    locked={!v.available}
                    onClick={() => sandboxDispatch(room.id, v.name)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Characters category ("You see people:") */}
          {room.characters.length > 0 && (
            <div>
              <SectionLabel>You see people:</SectionLabel>
              {giveMode && (
                <div style={{ marginBottom: "0.4rem", padding: "0.4rem", background: "rgba(100, 200, 120, 0.12)", border: "1px solid rgba(100, 200, 120, 0.2)", borderRadius: "4px", fontSize: "0.75rem" }}>
                  <span>Select recipient...</span>
                  <button
                    onClick={cancelGive}
                    className="theme-button"
                    style={{
                      display: "block",
                      marginTop: "0.3rem",
                      fontSize: "0.65rem",
                      padding: "0.1rem 0.3rem",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {room.characters.map((charId) => (
                <div
                  key={charId}
                  onClick={giveMode ? () => completeGive(charId) : undefined}
                  style={{
                    cursor: giveMode ? "pointer" : undefined,
                    padding: giveMode ? "0.1rem" : undefined,
                    background: giveMode ? "rgba(100, 200, 120, 0.06)" : undefined,
                    borderRadius: "4px",
                    transition: "background 0.15s ease",
                  }}
                >
                  <EntityRow
                    id={charId}
                    name={sandboxEngine?.getEntityName(charId) ?? charId}
                    verbs={sandboxEngine?.getEntityVerbEntries(charId) ?? []}
                    disabled={hasNarration || giveMode}
                    onVerb={sandboxDispatch}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Objects category ("You see objects:") */}
          {room.objects.length > 0 && (
            <div>
              <SectionLabel>You see objects:</SectionLabel>
              {room.objects.map((objId) => (
                <EntityRow
                  key={objId}
                  id={objId}
                  name={sandboxEngine?.getEntityName(objId) ?? objId}
                  verbs={sandboxEngine?.getEntityVerbEntries(objId) ?? []}
                  disabled={hasNarration}
                  onVerb={sandboxDispatch}
                />
              ))}
            </div>
          )}

          {/* Exits category ("You see exits:") */}
          {room.exits.length > 0 && (
            <div>
              <SectionLabel>You see exits:</SectionLabel>
              {room.exits.map((exit) => (
                <EntityRow
                  key={exit.target}
                  id={exit.target}
                  name={exit.dir
                    ? `${sandboxEngine?.getExitName(exit.target) ?? exit.target} (${exit.dir})`
                    : sandboxEngine?.getExitName(exit.target) ?? exit.target}
                  verbs={exit.verbs.map((v) => ({ name: v, available: exit.available }))}
                  disabled={hasNarration}
                  onVerb={sandboxDispatch}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Floating Hover Inventory Container (Right) */}
      <div
        onMouseEnter={() => setIsInventoryHovered(true)}
        onMouseLeave={() => setIsInventoryHovered(false)}
        style={{
          position: "absolute",
          right: "1.5rem",
          top: "1.5rem",
          bottom: "1.5rem",
          width: "250px",
          pointerEvents: "auto",
          zIndex: 50,
        }}
      >
        {/* Sliding Inventory Panel Card */}
        <div
          className="theme-panel"
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            padding: "0.75rem",
            overflow: "visible",
            transform: showInventoryPanel ? "translateX(0)" : "translateX(calc(100% + 1.5rem))",
            transition: "transform 0.3s cubic-bezier(0.25, 1, 0.5, 1)",
          }}
        >
          {/* Border indicator tab "INVENTORY" attached to left edge of the panel */}
          <div
            onClick={() => setInventoryPinned((p) => !p)}
            title={inventoryPinned ? "Unpin inventory" : "Pin inventory open"}
            style={{
              position: "absolute",
              left: "-25px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "25px",
              padding: "1rem 0.25rem",
              background: inventoryPinned ? "var(--theme-accent, #7ec8e3)" : "var(--theme-bg)",
              border: "var(--theme-border)",
              borderRight: "none",
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
              color: inventoryPinned ? "#111" : "var(--theme-text-color, #ccc)",
              fontSize: "0.65rem",
              fontWeight: "bold",
              writingMode: "vertical-lr",
              textOrientation: "mixed",
              cursor: "pointer",
              userSelect: "none",
              letterSpacing: "0.15em",
              boxShadow: "var(--theme-box-shadow)",
              fontFamily: "var(--theme-font-family)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              transition: "background 0.15s ease, color 0.15s ease",
            }}
          >
            INVENTORY
          </div>

          <InventoryPanel
            items={inventory}
            capacity={capacity}
            disabled={hasNarration}
            onVerb={sandboxDispatch}
            assembleMode={assembleMode}
            assembleFirst={assembleFirst}
            onAssembleSelect={completeAssemble}
            onCancelAssemble={cancelAssemble}
            getVerbEntries={(id) => sandboxEngine?.getEntityVerbEntries(id) ?? []}
          />
        </div>
      </div>
    </div>
  );
}
